import type { ComputerControlBridge } from '../app/components/computer-control.js';
import type { LocalIdentityBridge } from '../app/local-identity-types.js';
import type { BluetoothChooserBridge } from '../device-ble/transport.js';
import type { MacWifiBridge } from '../api/wifi-types.js';
import { ApiClient, ApiError, RequestAbortError } from '../api/client.js';
import { ASIS_MOBILE_SERVER_BASE, getMobileBridge } from '../platform/mobile-bridge.js';
import type { AsisMobileBridge } from '../platform/mobile-bridge.js';

export interface SessionUser { id: string; username: string; email?: string | null; displayName: string }
export interface LocalStatus {
  whisper: { available: boolean; installed: boolean; bytes: number };
  speaker: { available: boolean; installed: boolean; bytes: number };
  tts: { available: boolean };
  biometrics: { face: boolean; voice: boolean };
  experimental: boolean;
}
export type DesktopTidyType = 'Imágenes' | 'Documentos' | 'Audio' | 'Vídeos' | 'Comprimidos' | 'Otros';
export interface ComputerStatus {
  cpu: { logicalCores: number; usagePercent: number };
  memory: { totalBytes: number; freeBytes: number; usedBytes: number; usagePercent: number };
  disk: { totalBytes: number | null; freeBytes: number | null; usedBytes: number | null; usagePercent: number | null };
  battery: { onBatteryPower: boolean | null };
  uptimeSeconds: number;
  measuredAt: string;
}
export interface DesktopTidyPlan { id: string; total: number; counts: Record<DesktopTidyType, number>; files: Array<{ name: string; type: DesktopTidyType }>; omittedCount?: number; recoveryManifestId?: string | null }
export interface DesktopTidyApplyResult { ok: boolean; movedCount: number; plannedCount: number; manifestId: string | null; error: string | null }
export interface DesktopTidyUndoResult { ok: boolean; restoredCount: number; failedCount: number; manifestId: string | null }
export interface DesktopBridge {
  computerControl?: ComputerControlBridge;
  bluetooth?: BluetoothChooserBridge;
  /** Mac app only: saved Wi-Fi names and, after a click, one Keychain password. */
  wifi?: MacWifiBridge;
  local?: LocalIdentityBridge;
  computer?: { status(): Promise<ComputerStatus> };
  desktop?: {
    plan(): Promise<DesktopTidyPlan>;
    apply(planId: string): Promise<DesktopTidyApplyResult>;
    undo(manifestId: string): Promise<DesktopTidyUndoResult>;
  };
  openCreateApps?(url: string): Promise<void>;
  getConfig(): Promise<{ serverUrl: string }>;
  setServerUrl(url: string): Promise<unknown>;
  getSession(): Promise<{ token: string; user: SessionUser } | null>;
  saveSession(payload: { token: string; user: SessionUser; expiresAt?: string }): Promise<unknown>;
  clearSession(): Promise<unknown>;
  getVersion(): Promise<string>;
}
export type AuthState =
  | { kind: 'unknown' }
  | { kind: 'local' }
  | { kind: 'anonymous'; reason: 'fresh' | 'logged_out' | 'expired' | 'switched' | 'setup_required' | 'offline' | 'secure_session_unavailable' | 'logout_pending' }
  | { kind: 'authenticated'; user: SessionUser; serverBase: string };
export interface ControllerOptions {
  defaultBrowserBase: string;
  defaultDesktopBase: string;
  serverBaseStore?: { read(): string | null; write(base: string): void; clear(): void };
  bridge?: DesktopBridge | null;
  mobileBridge?: AsisMobileBridge | null;
  fetchImpl?: typeof fetch;
  now?: () => number;
  logger?: (msg: string, data?: unknown) => void;
  onStopVoice?: () => void;
}
declare global { interface Window { asisDesktop?: DesktopBridge } }
const LOOPBACK_HOSTS = new Set(['localhost', '127.0.0.1', '::1']);
export function isValidServerBase(raw: string): boolean {
  if (typeof raw !== 'string' || !raw) return false;
  try {
    const u = new URL(raw);
    if (u.username || u.password || (u.pathname !== '/' && u.pathname !== '') || u.search || u.hash) return false;
    if (u.protocol === 'https:') return true;
    return u.protocol === 'http:' && LOOPBACK_HOSTS.has(u.hostname.replace(/^\[|\]$/gu, ''));
  } catch { return false; }
}
const normalize = (raw: string) => { const u = new URL(raw); return `${u.protocol}//${u.host}`; };
export type StateListener = (state: AuthState) => void;

export class SessionController {
  private readonly opts: ControllerOptions;
  private readonly listeners = new Set<StateListener>();
  private state: AuthState = { kind: 'unknown' };
  private token: string | null = null;
  private user: SessionUser | null = null;
  private serverBase: string;
  private inElectron = false;
  private hasBridge = false;
  private bridge: DesktopBridge | null = null;
  private mobileBridge: AsisMobileBridge | null = null;
  private secureStoreUnavailable = false;
  private pendingMobileLogout: { token: string | null; base: string; revoke: boolean; clear: boolean } | null = null;
  private generation = 0;
  private authRequest: Promise<unknown> | null = null;
  private loggingOut = false;
  private bridgeQueue: Promise<unknown> = Promise.resolve();
  private api: ApiClient;
  constructor(options: ControllerOptions) {
    this.opts = options;
    this.serverBase = options.defaultBrowserBase;
    this.api = this.makeApi(this.serverBase);
  }
  private makeApi(base: string): ApiClient {
    return new ApiClient({ base, getToken: () => this.token, browserSession: () => this.usesBrowserSession(), onUnauthorized: () => this.handle401(), onNetworkError: e => this.opts.logger?.('net', e.message), fetchImpl: this.opts.fetchImpl });
  }
  private isValidSavedSession(saved: unknown): saved is { token: string; user: SessionUser } {
    if (!saved || typeof saved !== 'object') return false;
    const s = saved as any;
    if (typeof s.token !== 'string' || !s.token) return false;
    if (!s.user || typeof s.user !== 'object') return false;
    const u = s.user as any;
    return typeof u.id === 'string' && typeof u.username === 'string' && typeof u.displayName === 'string';
  }
  onState(fn: StateListener): () => void { this.listeners.add(fn); fn(this.state); return () => this.listeners.delete(fn); }
  getState(): AuthState { return this.state; }
  getServerBase(): string { return this.serverBase; }
  getUser(): SessionUser | null { return this.user; }
  getToken(): string | null { return this.token; }
  getApi(): ApiClient { return this.api; }
  isElectron(): boolean { return this.inElectron; }
  isMobile(): boolean { return this.mobileBridge !== null; }
  getMobilePlatform(): 'ios' | 'android' | null { return this.mobileBridge?.platform ?? null; }
  hasBridgeSession(): boolean { return this.hasBridge; }
  usesBrowserSession(): boolean {
    return !this.inElectron && this.mobileBridge === null && this.serverBase.startsWith('https:') && this.serverBase === normalize(this.opts.defaultBrowserBase);
  }
  private bridgeAction(action: () => Promise<unknown>): Promise<unknown> {
    const next = this.bridgeQueue.then(action, action);
    this.bridgeQueue = next.catch(() => undefined);
    return next;
  }
  async retry(): Promise<AuthState> {
    if (this.pendingMobileLogout) { await this.logout(); return this.state; }
    return this.secureStoreUnavailable ? this.bootstrap() : this.probe();
  }
  async getVersion(): Promise<string | null> {
    if (this.bridge) return this.bridge.getVersion();
    if (this.mobileBridge) return this.mobileBridge.getVersion();
    return null;
  }
  updateDisplayName(name: string): void { if (this.user) this.user = { ...this.user, displayName: name }; }
  async bootstrap(): Promise<AuthState> {
    if (this.pendingMobileLogout) return this.retry();
    this.secureStoreUnavailable = false;
    this.mobileBridge = this.opts.mobileBridge !== undefined ? this.opts.mobileBridge : (typeof window !== 'undefined' ? getMobileBridge() : null);
    this.bridge = this.mobileBridge ? null : (this.opts.bridge !== undefined ? this.opts.bridge : (typeof window !== 'undefined' ? window.asisDesktop ?? null : null));
    this.inElectron = !!this.bridge;
    this.hasBridge = this.inElectron || this.mobileBridge !== null;
    if (this.mobileBridge) {
      this.serverBase = normalize(ASIS_MOBILE_SERVER_BASE);
      this.api.setBase(this.serverBase);
      try {
        const saved = await this.mobileBridge.getSession();
        if (this.isValidSavedSession(saved)) {
          this.token = saved.token;
          this.user = saved.user;
        }
      } catch {
        this.secureStoreUnavailable = true;
        this.clearLocal();
        this.opts.logger?.('mobile_bootstrap_failed', 'mobile_session_unavailable');
        this.setState({ kind: 'anonymous', reason: 'secure_session_unavailable' });
        return this.state;
      }
    } else if (this.bridge) {
      try {
        const config = await this.bridge.getConfig();
        this.serverBase = config?.serverUrl && isValidServerBase(config.serverUrl) ? normalize(config.serverUrl) : normalize(this.opts.defaultDesktopBase);
        const saved = await this.bridge.getSession();
        if (this.isValidSavedSession(saved)) {
          this.token = saved.token;
          this.user = saved.user;
        }
      } catch (err) {
        this.opts.logger?.('bridge_bootstrap_failed', (err as Error).message);
        this.serverBase = normalize(this.opts.defaultDesktopBase);
      }
    } else {
      const stored = this.opts.serverBaseStore?.read();
      if (stored && isValidServerBase(stored) && (!this.opts.defaultBrowserBase.startsWith('https:') || normalize(stored) === normalize(this.opts.defaultBrowserBase))) this.serverBase = normalize(stored);
    }
    this.api.setBase(this.serverBase);
    return this.probe();
  }
  async login(email: string, password: string): Promise<SessionUser> { return this.authenticate(() => this.api.login({ email, password })); }
  async register(args: { email: string; displayName: string; password: string }): Promise<SessionUser> {
    if (args.password.length < 12) throw new ApiError(400, 'weak_password', 'La contraseña debe tener al menos 12 caracteres', null);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(args.email)) throw new ApiError(400, 'invalid_email', 'Escribe un correo válido.', null);
    if (!args.displayName.trim() || args.displayName.length > 64) throw new ApiError(400, 'invalid_displayname', 'Nombre visible 1-64 caracteres', null);
    return this.authenticate(() => this.api.register({ email: args.email, displayName: args.displayName, password: args.password }));
  }
  async setup(args: { setupToken: string; email: string; displayName: string; password: string }): Promise<SessionUser> {
    if (args.password.length < 12) throw new ApiError(400, 'weak_password', 'La contraseña debe tener al menos 12 caracteres', null);
    return this.authenticate(() => this.api.setup(args));
  }
  private async authenticate(request: () => Promise<{ token?: string; session?: 'browser'; user: SessionUser; expiresAt?: string }>): Promise<SessionUser> {
    if (this.pendingMobileLogout) throw new ApiError(409, 'logout_pending', 'Completa el cierre de la sesión anterior antes de entrar.', null);
    if (this.secureStoreUnavailable) throw new ApiError(503, 'secure_session_unavailable', 'No se pudo abrir tu sesión guardada. Reintenta la conexión antes de iniciar sesión.', null);
    if (this.loggingOut || this.state.kind !== 'anonymous') throw new ApiError(409, 'not_ready', 'Espera a que termine la conexión con el servidor.', null);
    const gen = this.generation;
    const pending = request(); this.authRequest = pending;
    let result: Awaited<ReturnType<typeof request>>;
    try { result = await pending; } finally { if (this.authRequest === pending) this.authRequest = null; }
    if (gen !== this.generation) throw new RequestAbortError();
    const validCredential = this.usesBrowserSession() ? result.session === 'browser' && result.token === undefined : typeof result.token === 'string' && !!result.token;
    if (!validCredential || !result.user || !['id', 'username', 'displayName'].every(key => typeof result.user[key as keyof SessionUser] === 'string')) throw new ApiError(502, 'invalid_session', 'El servidor no devolvió una sesión válida.', null);
    this.token = result.token ?? null; this.user = result.user;
    const secureBridge = this.mobileBridge ?? this.bridge;
    if (secureBridge) {
      try { await this.bridgeAction(() => secureBridge.saveSession({ token: result.token!, user: result.user, ...(result.expiresAt ? { expiresAt: result.expiresAt } : {}) })); }
      catch (error) {
        if (this.mobileBridge && gen === this.generation) {
          await this.logout().catch(() => undefined);
          throw new ApiError(503, 'secure_session_unavailable', 'No se pudo guardar la sesión de forma segura. Vuelve a intentarlo.', null);
        }
        throw error;
      }
    }
    if (gen !== this.generation) throw new RequestAbortError();
    this.setState({ kind: 'authenticated', user: result.user, serverBase: this.serverBase });
    return result.user;
  }
  async logout(): Promise<void> {
    if (this.loggingOut) throw new ApiError(409, 'logout_pending', 'El cierre de sesión sigue en curso.', null);
    const token = this.token; const oldBase = this.serverBase;
    const browser = this.usesBrowserSession(); const pendingLogin = this.authRequest;
    ++this.generation; this.loggingOut = true;
    this.stopPrivate(); this.clearLocal(); this.api.invalidate();
    if (this.mobileBridge) {
      const pending = this.pendingMobileLogout ?? { token, base: oldBase, revoke: token !== null, clear: true };
      this.pendingMobileLogout = pending;
      this.setState({ kind: 'anonymous', reason: 'logout_pending' });
      try {
        // The pending bearer is available only to this revocation request;
        // normal API requests have already lost access to it via clearLocal().
        await Promise.allSettled([
          (async () => {
            if (!pending.revoke) return;
            const revoke = new ApiClient({ base: pending.base, getToken: () => pending.token, onUnauthorized: () => undefined, fetchImpl: this.opts.fetchImpl });
            try { await revoke.logout(); }
            catch (error) { if (!(error instanceof ApiError && error.status === 401)) throw error; }
            pending.revoke = false; pending.token = null;
          })(),
          (async () => {
            if (!pending.clear) return;
            await this.bridgeAction(() => this.mobileBridge!.clearSession());
            pending.clear = false;
          })(),
        ]);
        if (pending.revoke || pending.clear) throw new ApiError(503, 'logout_pending', 'La sesión ya no se usa en esta app, pero falta confirmar su cierre. Reinténtalo cuando haya conexión y acceso al almacenamiento seguro.', null);
        this.pendingMobileLogout = null;
        this.setState({ kind: 'anonymous', reason: 'logged_out' });
        return;
      } finally { this.loggingOut = false; }
    }
    this.setState({ kind: 'anonymous', reason: 'logged_out' });
    const revokeApi = token || browser ? new ApiClient({ base: oldBase, getToken: () => token, browserSession: () => browser, onUnauthorized: () => undefined, fetchImpl: this.opts.fetchImpl }) : null;
    try {
      // Revoke after a pending cookie login response so a late cookie cannot
      // survive the visible logout.
      if (browser && pendingLogin) await pendingLogin.catch(() => undefined);
      const secureBridge = this.mobileBridge ?? this.bridge;
      await Promise.all([
        revokeApi?.logout(),
        secureBridge ? this.bridgeAction(() => secureBridge.clearSession()) : Promise.resolve()
      ]);
    } finally { this.loggingOut = false; }
  }
  async switchServerBase(raw: string): Promise<AuthState> {
    if (!isValidServerBase(raw)) throw new ApiError(0, 'invalid_base', 'Servidor no válido', null);
    const normalized = normalize(raw);
    if (this.mobileBridge) {
      if (normalized !== normalize(ASIS_MOBILE_SERVER_BASE)) throw new ApiError(400, 'mobile_fixed_server', 'El servidor nativo es fijo.', null);
      return this.state;
    }
    if (!this.inElectron && this.opts.defaultBrowserBase.startsWith('https:') && normalized !== normalize(this.opts.defaultBrowserBase)) throw new ApiError(400, 'same_origin_required', 'En el móvil, abre la dirección del otro servidor en el navegador para iniciar sesión allí.', null);
    if (normalized === this.serverBase) return this.state;
    const gen = ++this.generation; this.stopPrivate(); this.clearLocal(); this.api.invalidate();
    this.setState({ kind: 'unknown' });
    try {
      if (this.bridge) await this.bridgeAction(async () => { await this.bridge!.clearSession(); await this.bridge!.setServerUrl(normalized); });
      else this.opts.serverBaseStore?.write(normalized);
    } catch (err) { if (gen === this.generation) this.setState({ kind: 'anonymous', reason: 'offline' }); throw err; }
    if (gen !== this.generation) return this.state;
    this.serverBase = normalized; this.api = this.makeApi(normalized);
    return this.probe();
  }
  private setState(state: AuthState): void { this.state = state; for (const listener of this.listeners) { try { listener(state); } catch (err) { this.opts.logger?.('listener_error', (err as Error).message); } } }
  private clearLocal(): void { this.token = null; this.user = null; }
  private stopPrivate(): void { this.opts.onStopVoice?.(); }
  private handle401(): void {
    if (this.pendingMobileLogout) return;
    ++this.generation; this.stopPrivate(); this.clearLocal(); this.api.invalidate();
    const secureBridge = this.mobileBridge ?? this.bridge;
    const isNative = this.mobileBridge !== null && this.bridge === null;
    if (secureBridge) void this.bridgeAction(() => secureBridge.clearSession()).catch(err => {
      this.opts.logger?.('bridge_clear_failed', isNative ? 'bridge_clear_unavailable' : (err as Error).message);
    });
    this.setState({ kind: 'anonymous', reason: 'expired' });
  }
  private async probe(): Promise<AuthState> {
    const gen = this.generation;
    let status: { setupRequired: boolean; authRequired: boolean; version: number };
    try { status = await this.api.getStatus(); }
    catch { if (gen !== this.generation) return this.state; this.setState({ kind: 'anonymous', reason: 'offline' }); return this.state; }
    if (gen !== this.generation) return this.state;
    if (status.setupRequired) { this.clearLocal(); this.setState({ kind: 'anonymous', reason: 'setup_required' }); return this.state; }
    if (status.authRequired === false) { this.clearLocal(); this.setState({ kind: 'local' }); return this.state; }
    if ((this.token && this.user) || this.usesBrowserSession()) {
      try {
        const me = await this.api.getMe(undefined, true); if (gen !== this.generation) return this.state;
        this.user = me.user; this.setState({ kind: 'authenticated', user: me.user, serverBase: this.serverBase }); return this.state;
      } catch (error) {
        if (gen !== this.generation) return this.state;
        if (this.mobileBridge) {
          if (error instanceof ApiError && error.status === 401) {
            this.handle401();
            await this.bridgeQueue;
          } else {
            // A network/server failure is not evidence that the saved token
            // was revoked. Keep it for an explicit retry of /auth/me.
            this.setState({ kind: 'anonymous', reason: 'offline' });
          }
          return this.state;
        }
        this.clearLocal();
      }
    }
    this.setState({ kind: 'anonymous', reason: 'fresh' }); return this.state;
  }
}
